import { useState, useEffect, useCallback } from 'react';

const cache = {
  invites: { data: [], fetchedAt: 0 },
  applications: { data: [], fetchedAt: 0 },
};

const inFlight = {
  invites: null,
  applications: null,
};

// TTL constants
const INVITES_TTL_MS = 30000; // 30 seconds
const APPLICATIONS_TTL_MS = 30000; // 30 seconds

// Listeners: any mounted hook instance registers a forceUpdate here
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchInvites(force = false) {
  const now = Date.now();
  if (!force && cache.invites.fetchedAt && now - cache.invites.fetchedAt < INVITES_TTL_MS) {
    return cache.invites.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlight.invites) {
    return inFlight.invites;
  }

  // Assign in-flight before any await so parallel callers share one request.
  inFlight.invites = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/campaigns/invites`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache.invites = { data, fetchedAt: Date.now() };
        return data;
      }
      throw new Error('Failed to fetch invites');
    } finally {
      inFlight.invites = null;
    }
  })();

  return inFlight.invites;
}

async function fetchApplications(force = false) {
  const now = Date.now();
  if (!force && cache.applications.fetchedAt && now - cache.applications.fetchedAt < APPLICATIONS_TTL_MS) {
    return cache.applications.data;
  }

  // Dedupe: if a request is already in-flight, wait for it
  if (inFlight.applications) {
    return inFlight.applications;
  }

  const promise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/campaign/applications`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        cache.applications = { data, fetchedAt: Date.now() };
        return data;
      }
      throw new Error('Failed to fetch applications');
    } finally {
      inFlight.applications = null;
    }
  })();

  inFlight.applications = promise;
  return promise;
}

/**
 * Shared hook for influencer collaborations (invites + applications).
 * Uses module-level cache so data is shared across all mounted components.
 *
 * @returns {{ invites: Array, applications: Array, loading: boolean, error: string|null, refresh: Function, lastUpdated: { invites: number, applications: number } }}
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetInfluencerCollaborationsCache() {
  cache.invites = { data: [], fetchedAt: 0 };
  cache.applications = { data: [], fetchedAt: 0 };
  inFlight.invites = null;
  inFlight.applications = null;
}

/** Shared invites fetch with module TTL + single-flight (used by chat + dashboard). */
export async function fetchInfluencerInvites(force = false) {
  return fetchInvites(force);
}

/** Shared applications fetch with module TTL + single-flight (used by dashboard omnibar). */
export async function fetchInfluencerApplications(force = false) {
  return fetchApplications(force);
}

export default function useInfluencerCollaborations({ enabled = true } = {}) {
  const [invites, setInvites] = useState(cache.invites.data);
  const [applications, setApplications] = useState(cache.applications.data);
  const [loading, setLoading] = useState(
    () => enabled && (!cache.invites.fetchedAt || !cache.applications.fetchedAt),
  );
  const [error, setError] = useState(null);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    setInvites(cache.invites.data);
    setApplications(cache.applications.data);
  }, []);

  // Register this instance as a listener
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(syncFromCache);
    return () => {
      listeners.delete(syncFromCache);
    };
  }, [enabled, syncFromCache]);

  // Refresh function callable by consumers
  const refresh = useCallback(async ({
    invites: refreshInvites = true,
    applications: refreshApps = true,
    force = false,
  } = {}) => {
    try {
      setError(null);
      const promises = [];

      if (refreshInvites) {
        promises.push(
          fetchInvites(force).then((data) => {
            setInvites(data);
          }),
        );
      }

      if (refreshApps) {
        promises.push(
          fetchApplications(force).then((data) => {
            setApplications(data);
          }),
        );
      }

      await Promise.all(promises);
      notifyListeners();
    } catch (err) {
      setError(err.message || 'Failed to load data. Please try again.');
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadData = async () => {
      // If we already have cached data, show it immediately
      if (cache.invites.fetchedAt && cache.applications.fetchedAt) {
        setInvites(cache.invites.data);
        setApplications(cache.applications.data);
        setLoading(false);

        // Still refresh in background if stale
        const now = Date.now();
        const invitesStale = now - cache.invites.fetchedAt >= INVITES_TTL_MS;
        const appsStale = now - cache.applications.fetchedAt >= APPLICATIONS_TTL_MS;

        if (invitesStale || appsStale) {
          try {
            await refresh({ invites: invitesStale, applications: appsStale, force: false });
          } catch {
            // Ignore background refresh errors
          }
        }
        return;
      }

      setLoading(true);
      try {
        const [invitesData, appsData] = await Promise.all([
          fetchInvites(false),
          fetchApplications(false),
        ]);

        if (!cancelled) {
          setInvites(invitesData);
          setApplications(appsData);
          notifyListeners();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load data. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    invites,
    applications,
    loading,
    error,
    refresh,
    lastUpdated: {
      invites: cache.invites.fetchedAt,
      applications: cache.applications.fetchedAt,
    },
  };
}
